import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { Link } from "../lib/router.js";
import { IconSparkles } from "./icons.js";

interface Proposal {
  id: string;
  kind: string;
  title: string;
  rationale: string;
  source: string;
  status: string;
}

/**
 * Surfaces pending plan-adjustment proposals. Accepting one NEVER silently
 * changes a program, target, or any safety-sensitive setting — the server
 * applies only small, whitelisted effects (e.g. marking a goal achieved) and
 * records the decision either way. "Ask Coach" opens the chat to talk it through.
 */
export function ProposalsCard({
  onResolved,
  showGenerate = false
}: {
  onResolved?: () => void;
  showGenerate?: boolean;
}) {
  const [proposals, setProposals] = useState<Proposal[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try { setProposals((await api.get<{ proposals: Proposal[] }>("/api/proposals?status=pending")).proposals); }
    catch { setProposals([]); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function resolve(id: string, decision: "accepted" | "declined") {
    setBusy(id);
    try {
      await api.post(`/api/proposals/${id}/resolve`, { decision });
      await load();
      onResolved?.();
    } finally {
      setBusy(null);
    }
  }

  async function generate() {
    setGenerating(true);
    try {
      await api.post("/api/proposals/generate");
      await load();
    } catch { /* ignore */ } finally {
      setGenerating(false);
    }
  }

  const list = proposals ?? [];
  if (list.length === 0 && !showGenerate) return null;

  return (
    <div className="card proposals-card">
      <div className="card-header">
        <span className="card-title"><IconSparkles width={16} height={16} /> Suggestions</span>
        {showGenerate && (
          <button className="btn btn-sm btn-ghost" onClick={generate} disabled={generating}>
            {generating ? "Thinking…" : "Ask Coach to review"}
          </button>
        )}
      </div>

      {list.length === 0 ? (
        <p className="muted text-sm" style={{ marginBottom: 0 }}>
          No suggestions right now. When ClovaForm notices a pattern worth adjusting, it'll propose a change here — you always decide.
        </p>
      ) : (
        <div className="stack">
          {list.map((p) => (
            <div className="proposal" key={p.id}>
              <div style={{ fontWeight: 600 }}>{p.title}</div>
              <p className="muted text-sm" style={{ margin: "4px 0 10px" }}>{p.rationale}</p>
              <div className="row wrap" style={{ gap: "var(--space-2)" }}>
                <button className="btn btn-sm btn-primary" onClick={() => resolve(p.id, "accepted")} disabled={busy === p.id}>Accept</button>
                <button className="btn btn-sm" onClick={() => resolve(p.id, "declined")} disabled={busy === p.id}>Not now</button>
                <Link to="/insights" className="btn btn-sm btn-ghost">Ask Coach</Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
